import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { UseFormReturn } from "react-hook-form";
import { Briefcase, Users, DoorOpen, UserCheck, Calendar, Megaphone, Handshake, CreditCard, Banknote, Sparkles, Trophy, Car, Database, MapPin, TrendingUp } from "lucide-react";

interface BusinessStepProps {
  form: UseFormReturn<any>;
}

const RadioRow = ({ value, label }: { value: string; label: string }) => (
  <label className="flex items-center gap-3 rounded-md border border-input bg-background px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors">
    <RadioGroupItem value={value} />
    <span className="text-sm">{label}</span>
  </label>
);

const CheckboxRow = ({
  value,
  label,
  current,
  onChange,
}: {
  value: string;
  label: string;
  current: string[];
  onChange: (next: string[]) => void;
}) => {
  const checked = current.includes(value);
  return (
    <label className="flex items-center gap-3 rounded-md border border-input bg-background px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors">
      <Checkbox
        checked={checked}
        onCheckedChange={(c) => {
          if (c) onChange([...current, value]);
          else onChange(current.filter((v) => v !== value));
        }}
      />
      <span className="text-sm">{label}</span>
    </label>
  );
};

const BusinessStep = ({ form }: BusinessStepProps) => {
  const hasFinancing = form.watch("hasFinancing");
  const hasProprietaryMethod = form.watch("hasProprietaryMethod");

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-primary" />
          Il Tuo Business
        </h2>
        <p className="text-muted-foreground">
          Aiutaci a capire il tuo punto di partenza e il tuo posizionamento sul mercato
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Employees */}
        <FormField
          control={form.control}
          name="employeeCount"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Numero di dipendenti *
              </FormLabel>
              <FormControl>
                <Input type="number" inputMode="numeric" min={0} placeholder="Es. 3" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Cabins */}
        <FormField
          control={form.control}
          name="cabinCount"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <DoorOpen className="w-4 h-4" /> Numero totale di cabine nel centro *
              </FormLabel>
              <FormControl>
                <Input type="number" inputMode="numeric" min={0} placeholder="Es. 2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Years in business */}
      <FormField
        control={form.control}
        name="yearsInBusiness"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Da quanti anni è aperto il centro? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="under_1" label="Meno di 1 anno" />
                <RadioRow value="1_to_3" label="Tra 1 e 3 anni" />
                <RadioRow value="3_to_7" label="Tra 3 e 7 anni" />
                <RadioRow value="over_7" label="Oltre 7 anni" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Center zone */}
      <FormField
        control={form.control}
        name="centerZone"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Zona del centro *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="city_center" label="Centro città" />
                <RadioRow value="semi_center" label="Semicentro" />
                <RadioRow value="suburb" label="Periferia" />
                <RadioRow value="small_town" label="Paese o piccolo comune" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Clients last 12 months */}
      <FormField
        control={form.control}
        name="clientsLast12Months"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Users className="w-4 h-4" /> Numero indicativo di clienti gestiti negli ultimi 12 mesi *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="under_200" label="Meno di 200" />
                <RadioRow value="200_500" label="Tra 200 e 500" />
                <RadioRow value="500_1000" label="Tra 500 e 1.000" />
                <RadioRow value="1000_2000" label="Tra 1.000 e 2.000" />
                <RadioRow value="over_2000" label="Oltre 2.000" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Database size */}
      <FormField
        control={form.control}
        name="databaseSize"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Database className="w-4 h-4" /> Quanti clienti hai attualmente nel tuo database / gestionale? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="under_500" label="Meno di 500" />
                <RadioRow value="500_1000" label="Tra 500 e 1.000" />
                <RadioRow value="1000_2000" label="Tra 1.000 e 2.000" />
                <RadioRow value="2000_5000" label="Tra 2.000 e 5.000" />
                <RadioRow value="over_5000" label="Oltre 5.000" />
                <RadioRow value="paper_only" label="Ho solo agenda cartacea" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Sales responsible (multi-select) */}
      <FormField
        control={form.control}
        name="salesResponsible"
        render={({ field }) => {
          const current: string[] = Array.isArray(field.value) ? field.value : [];
          return (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <UserCheck className="w-4 h-4" /> Chi si occupa della vendita/proposta pacchetti? *
              </FormLabel>
              <FormDescription>Seleziona tutte le opzioni che si applicano</FormDescription>
              <div className="grid gap-2">
                <CheckboxRow value="owner" label="Titolare" current={current} onChange={field.onChange} />
                <CheckboxRow value="dedicated_salesperson" label="Persona dedicata alla vendita" current={current} onChange={field.onChange} />
                <CheckboxRow value="cabin_operator" label="Operatrice in cabina" current={current} onChange={field.onChange} />
                <CheckboxRow value="multiple" label="Più figure combinate" current={current} onChange={field.onChange} />
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      {/* Paths or cards */}
      <FormField
        control={form.control}
        name="sellsPathsOrCards"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Vendi percorsi o card? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-3 gap-2">
                <RadioRow value="paths" label="Percorsi" />
                <RadioRow value="cards" label="Card" />
                <RadioRow value="both" label="Entrambi" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Financing */}
      <FormField
        control={form.control}
        name="hasFinancing"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Banknote className="w-4 h-4" /> Hai sistemi di rateizzazione/finanziamento in centro? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="yes" label="Sì" />
                <RadioRow value="no" label="No" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Financing providers (conditional, multi-select) */}
      {hasFinancing === "yes" && (
        <FormField
          control={form.control}
          name="financingProviders"
          render={({ field }) => {
            const current: string[] = Array.isArray(field.value) ? field.value : [];
            return (
              <FormItem>
                <FormLabel>Se sì, quale sistema di rateizzazione utilizzi?</FormLabel>
                <FormDescription>Seleziona tutte le opzioni che si applicano</FormDescription>
                <div className="grid sm:grid-cols-2 gap-2">
                  <CheckboxRow value="pagodil" label="PagoDil" current={current} onChange={field.onChange} />
                  <CheckboxRow value="pagolight" label="PagoLight" current={current} onChange={field.onChange} />
                  <CheckboxRow value="klarna" label="Klarna" current={current} onChange={field.onChange} />
                  <CheckboxRow value="scalapay" label="Scalapay" current={current} onChange={field.onChange} />
                  <CheckboxRow value="soisy" label="Soisy" current={current} onChange={field.onChange} />
                  <CheckboxRow value="compass" label="Compass" current={current} onChange={field.onChange} />
                  <CheckboxRow value="other" label="Altro" current={current} onChange={field.onChange} />
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}

      {/* Proprietary method */}
      <FormField
        control={form.control}
        name="hasProprietaryMethod"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Hai un tuo metodo innovativo con nome specifico? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid gap-2">
                <RadioRow value="yes" label="Ho un mio metodo" />
                <RadioRow value="no" label="No - non ho un mio metodo" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Proprietary method name (conditional) */}
      {hasProprietaryMethod === "yes" && (
        <FormField
          control={form.control}
          name="proprietaryMethodName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Qual è il nome del tuo metodo?</FormLabel>
              <FormControl>
                <Input placeholder="Es. Metodo XYZ" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Specialization */}
      <FormField
        control={form.control}
        name="centerSpecialization"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Trophy className="w-4 h-4" /> Il centro è riconosciuto come specializzato in un'area precisa? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid gap-2">
                <RadioRow value="basic" label="Estetica di base (nails/cerette ecc.)" />
                <RadioRow value="face" label="Specializzazione Viso" />
                <RadioRow value="body" label="Specializzazione Corpo/Dimagrimento" />
                <RadioRow value="advanced" label="Estetica avanzata a 360°" />
                <RadioRow value="epilation_laser" label="Specializzata in epilazione/laser" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Competitors */}
      <FormField
        control={form.control}
        name="specializedCompetitors"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Indica i tuoi principali competitor nella tua zona (massimo 3, nome o zona) *</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Es. Centro Bellezza Roma, Estetica Milano, ..."
                className="resize-none"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Price positioning */}
      <FormField
        control={form.control}
        name="pricePositioning"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Rispetto ai competitor, come definiresti i tuoi prezzi? *</FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-3 gap-2">
                <RadioRow value="cheaper" label="Sono più economica" />
                <RadioRow value="same" label="Ho gli stessi prezzi" />
                <RadioRow value="premium" label="Sono più Premium" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Parking */}
      <FormField
        control={form.control}
        name="parkingEase"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Car className="w-4 h-4" /> Quanto credi sia un problema il parcheggio per venire nel tuo centro? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="very_easy" label="Molto facile" />
                <RadioRow value="easy" label="Abbastanza facile" />
                <RadioRow value="neutral" label="Neutro" />
                <RadioRow value="difficult" label="Abbastanza difficile" />
                <RadioRow value="very_difficult" label="Molto difficile" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Social ads */}
      <FormField
        control={form.control}
        name="socialAdsExperience"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Megaphone className="w-4 h-4" /> Hai mai fatto pubblicità social per raccogliere contatti? (escluso Salone Vincente) *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="yes" label="Sì" />
                <RadioRow value="first_time" label="No - è la prima volta" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Marketing agency */}
      <FormField
        control={form.control}
        name="marketingAgencyExperience"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <Handshake className="w-4 h-4" /> Hai già lavorato con agenzie di marketing in passato? *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="yes" label="Sì" />
                <RadioRow value="no" label="No - è la prima volta" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Monthly revenue */}
      <FormField
        control={form.control}
        name="monthlyRevenueBeforeUs"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Fatturato medio MENSILE prima di iniziare con Salone Vincente *
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid sm:grid-cols-2 gap-2">
                <RadioRow value="under_5k" label="Sotto i 5.000€" />
                <RadioRow value="5k_10k" label="Tra 5.000€ e 10.000€" />
                <RadioRow value="10k_20k" label="Tra 10.000€ e 20.000€" />
                <RadioRow value="20k_40k" label="Tra 20.000€ e 40.000€" />
                <RadioRow value="over_40k" label="Oltre 40.000€" />
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default BusinessStep;
